import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RequireAuth } from "@/lib/auth/RequireAuth";
import {
  BoltTaskCompactGrid,
  openTaskExecutionDialog,
  TaskExecutionModuleFormRegistry,
  TaskExecutionModuleRegistry,
} from "@bolt/task";
import { useAuthHeadersRequired } from "@/lib/auth/authUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import variables from "@/config/variables";
import "./ConsumerPortalTasksPage.scss";
import { DocumentTemplateTaskExecutionTypeModule } from "@bolt/documenttemplates";
import { FinancialApplicationExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/FinancialApplicationExpressionSchemaTaskExecutionTypeForm";
import { JointAccountOwnerExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/JointAccountOwnerExpressionSchemaTaskExecutionTypeForm";
import { KeyPersonExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/KeyPersonExpressionSchemaTaskExecutionTypeForm";

export const ConsumerPortalTasksPage: React.FC = () => {
  const getAuthRequestHeader = useAuthHeadersRequired();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const formRegistry: TaskExecutionModuleFormRegistry = {
    FinancialApplicationExpressionSchema: {
      FormComponent: FinancialApplicationExpressionSchemaTaskExecutionTypeForm,
    },
  };

  const taskExecutionTypeModuleRegistry: TaskExecutionModuleRegistry = {
    DocumentTemplateTaskExecutionType: DocumentTemplateTaskExecutionTypeModule,
  };

  // Handle taskId URL parameter
  useEffect(() => {
    const taskId = searchParams.get("taskId");
    if (taskId) {
      openTaskExecutionDialog({
        taskId,
        apiBaseUrl: variables.apiBaseUrl,
        getAuthRequestHeader,
        formRegistry,
        taskExecutionModuleRegistry: taskExecutionTypeModuleRegistry,
        commentPermissions: {
          canComment: true,
          canEdit: false,
          canPin: false,
          canDelete: false,
        },
      });
    }
  }, [searchParams, getAuthRequestHeader, setSearchParams, variables.apiBaseUrl]);

  const handleBackToPortal = () => {
    navigate("/customer/portal");
  };

  return (
    <RequireAuth clientType="consumer">
      <div className="consumer-portal-tasks-page">
        <div className="consumer-portal-tasks-page__header">
          <div className="consumer-portal-tasks-page__header-content">
            <div className="consumer-portal-tasks-page__welcome">
              <h1 className="consumer-portal-tasks-page__title">My Tasks</h1>
              <p className="consumer-portal-tasks-page__subtitle">
                View and complete tasks for your applications
              </p>
            </div>
            <div className="consumer-portal-tasks-page__actions">
              <button
                className="consumer-portal-tasks-page__back-button"
                onClick={handleBackToPortal}
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  className="consumer-portal-tasks-page__back-icon"
                />
                Back to Portal
              </button>
            </div>
          </div>
        </div>

        <div className="consumer-portal-tasks-page__content">
          <div className="consumer-portal-tasks-page__breadcrumb">
            <button
              className="consumer-portal-tasks-page__breadcrumb-link"
              onClick={handleBackToPortal}
            >
              Consumer Portal
            </button>
            <span className="consumer-portal-tasks-page__breadcrumb-separator">/</span>
            <span className="consumer-portal-tasks-page__breadcrumb-current">My Tasks</span>
          </div>

          <div className="consumer-portal-tasks-page__grid-container">
            <BoltTaskCompactGrid
              apiBaseUrl={variables.apiBaseUrl}
              gridKey="consumer-tasks-grid"
              getAuthRequestHeader={getAuthRequestHeader}
              className="consumer-portal-tasks-grid"
              isTaskOverviewReadOnly={true}
              formRegistry={{
                FinancialApplicationExpressionSchema: {
                  FormComponent: FinancialApplicationExpressionSchemaTaskExecutionTypeForm,
                },
                JointAccountOwnerExpressionSchema: {
                  FormComponent: JointAccountOwnerExpressionSchemaTaskExecutionTypeForm,
                },
                KeyPersonExpressionSchema: {
                  FormComponent: KeyPersonExpressionSchemaTaskExecutionTypeForm,
                },
              }}
              taskExecutionModuleRegistry={{
                DocumentTemplateTaskExecutionType: DocumentTemplateTaskExecutionTypeModule,
              }}
              taskExecutionLeftPanelIsDocked={true}
              taskExecutionRightPanelIsDocked={false}
              commentPermissions={{
                canComment: true,
                canEdit: false,
                canPin: false,
                canDelete: false,
              }}
            />
          </div>
        </div>
      </div>
    </RequireAuth>
  );
};

export default ConsumerPortalTasksPage;
